import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Form, Modal } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CANCELLATION_WITHOUT_DOCS_PAYERS,
  FILE_UPLOAD_ERROR_MESSAGE, HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW,
  NEW_UPLOAD_STATUS, NON_AGREE_ERROR_MESSAGE, NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW,
} from '../../../../shared/constants/crmConstants';
import useGetPasswordExpireNotification from '../../../../shared/hooks/useGetPasswordExpireNotification';
import { RootState } from '../../../../store';
import { isCancelClicked, reloadCurrentPage, setClaimData, setReasonForCancellation } from '../../../../store/actions/claimDetailsAction';
import { submitCancelClaim } from '../../../../store/actions/newAdmissionAction';
import { NewAdmissionState } from '../../../../store/models/newAdmissionModel';
import { PayerInitialState } from '../../../../store/models/payerListModel';
import { centeredNotification } from '../../../../utils/utility';
import {
  customGTMEvent, documentUploadFlow, getCancellationMessage,
  getCancelModalTitle, getValidUploadedFiles, getLettersFromPatientDetails,
} from '../service/crmUtils';
import ApplicationDetail from '../../../../shared/services/applicationDetail.service';
import { HospitalState } from '../../../../store/models/hospitalModel';
import { cancelFlowMap, CANCELLATION_STATUS_ID } from '../../../../shared/constants/commonConstants';
import { useHistory, useParams } from 'react-router';
import { DeleteModal, RouteParams } from '../../../../shared/interfaces/claimDetailInterface';

const DeleteClaimModalNew = (props: DeleteModal): ReactElement => {
  const { isEnabled, setIsEnabled } = props;
  const { payer: { shortName, actionProperties } } = useSelector((state: RootState): PayerInitialState => state?.payerList);
  const { uploadedFiles } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const isPreAuthNewDesignEnabled = ApplicationDetail?.instance?.getIsPreAuthNewDesignEnabled();
  const { claimDetails } = useSelector((state: RootState): any => state.admissionDetails);
  const { selectedPatientDetail, hospitalSearchPatientDetails }: any =
    useSelector((state: RootState): HospitalState => state.hospital);
  const attachments = getLettersFromPatientDetails(selectedPatientDetail, hospitalSearchPatientDetails);
  const [currIndex, setCurrIndex] = useState(0);
  const params = useParams<RouteParams>();
  const [isChecked, setIsChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteForm] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  const isCancelWithoutDocument = CANCELLATION_WITHOUT_DOCS_PAYERS.includes(shortName);
  const isCare = shortName === 'CARE';
  const getNotification = useGetPasswordExpireNotification();
  const documentFlow = documentUploadFlow(isPreAuthNewDesignEnabled, attachments, claimDetails);
  const cancelActionProperty = JSON.parse(actionProperties || '{}')?.[CANCELLATION_STATUS_ID];
  const { claimDetail } = params || {};
  // Function to determine the cancel flow
  const getCancelFlowFromMap = (isCancelWithoutDocumentFlow: boolean, isCareFlow: boolean, isDocumentFlow: string): any => {
    return isCancelWithoutDocumentFlow
      ? cancelFlowMap.CANCEL_FLOW_WITHOUT_DOCS // Return the Heritage cancel flow
      : isCareFlow
        ? cancelFlowMap[
        // If the document flow is related to HIS, return HIS_CARE
        isDocumentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW
          ? 'HIS_CARE'
          : isDocumentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW // If the document flow is related to Non-HIS, return NON_HIS_CARE
            ? 'NON_HIS_CARE'
            : 'CARE' // Default case for Care
        ]
        : cancelFlowMap[
        isDocumentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW
          ? 'HIS' // If the document flow is related to HIS, return HIS cancel flow
          : isDocumentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW
            ? 'NON_HIS'  // If the document flow is related to Non-HIS, return NON_HIS cancel flow
            : 'DEFAULT' // Default cancel flow
        ];
  };
  const [componentNav, setComponentNav] = useState(getCancelFlowFromMap(isCancelWithoutDocument, isCare, documentFlow));

  useEffect((): any => {
    const getCancelFlow = getCancelFlowFromMap(isCancelWithoutDocument, isCare, documentFlow);
    setComponentNav(getCancelFlow);
  }, [isCancelWithoutDocument, isCare, documentFlow, attachments]);

  const gotoClaimDetail = (): void => history.push(`/claims/in-patient/detail/${claimDetail}`);
  const goNext = (toTab?: number): any => {
    if (toTab !== null || toTab !== undefined) {
      switch (true) {
        case (toTab === -1):
          gotoClaimDetail();
          deleteForm.resetFields();
          setCurrIndex(0);
          dispatch(setIsEnabled(false));
          setIsChecked(false);
          break;
        case (toTab !== -1):
          gotoIndex(toTab);
      }
    } else {
      gotoIndex();
    }
  };

  const newUploadedFiles = (documentFlow || isPreAuthNewDesignEnabled)
    ? uploadedFiles?.some((obj: any): boolean => !obj?.existingRecord)
    : uploadedFiles?.some(
      (obj: any): boolean => obj?.claimStatus === NEW_UPLOAD_STATUS,
    );
  const handleResponse = (notificationType: string, errorMessage?: string): void => {
    setIsLoading(false);
    const message = getCancellationMessage(notificationType, actionProperties, cancelActionProperty, errorMessage);
    centeredNotification(message, notificationType);
    dispatch(setIsEnabled(!isEnabled));
    setIsChecked(false);
    dispatch(reloadCurrentPage());
  };

  useEffect((): any => {
    if (!newUploadedFiles) {
      setIsChecked(false);
    }
  }, [newUploadedFiles]);

  const onSuccess = (): void => {
    setIsLoading(false);
    handleResponse('success');
    gotoClaimDetail();
    localStorage?.removeItem('claimDetailResponse');
    dispatch(setClaimData({}));
    dispatch(isCancelClicked(true));
  };
  const onError = (errorMessage: string): void => {
    setIsLoading(false);
    if (errorMessage === 'PASSWORD_EXPIRED') {
      centeredNotification('Password expired', 'error', getNotification);
    } else {
      handleResponse('error', errorMessage);
    }
  };

  const handleSubmit = (): void => {
    if (buttonText === 'Next') {
      goNext();
    } else {
      const validFiles = getValidUploadedFiles(uploadedFiles);
      deleteForm?.validateFields().then((): any => {
        if (!validFiles.length && !isCancelWithoutDocument) {
          setErrorMsg(FILE_UPLOAD_ERROR_MESSAGE);
        } else if (!isChecked && !isCancelWithoutDocument) {
          setErrorMsg(NON_AGREE_ERROR_MESSAGE);
        } else {
          setIsLoading(true);
          dispatch(submitCancelClaim(isChecked, onSuccess, onError));
          customGTMEvent({
            event: 'Submit Cancellation Request',
          });
        }
      });
    }
  };

  const handleCheckBox = (event: any): void => {
    const checked = event.target.checked;
    setIsChecked(checked && newUploadedFiles);
    if (checked) {
      setErrorMsg('');
      customGTMEvent({
        event: 'Cancellation: Select Checkbox',
      });
    }
  };

  const onReasonChange = (e: any): void => {
    dispatch(setReasonForCancellation(e?.target?.value));
    setIsChecked(true);
  };

  const gotoIndex = (index?: number): void => {
    if (componentNav[index !== undefined ? index : currIndex + 1]) {
      setCurrIndex(index !== undefined ? index : currIndex + 1);
    } else {
      setCurrIndex(currIndex);
    }
  };
  const { component: ClaimCancellationComponent, title, backFn, buttonText, modalWidth }: any = componentNav[currIndex];
  const modalTitleName = getCancelModalTitle(cancelActionProperty, title);
  const onBackPress = (): any => {
    // abhaForm.resetFields();
    if (componentNav[currIndex - 1]) {
      setCurrIndex(currIndex - 1);
    } else {
      setCurrIndex(currIndex);
    }
  };

  return (
    <Modal
      centered={true}
      visible={isEnabled}
      onCancel={(): void => goNext(-1)}
      footer={null}
      width={modalWidth}
      className={isPreAuthNewDesignEnabled ? 'custom-modal' : ''}
      title={((modalTitleName || backFn) ? <div>
        {
          backFn && <span><Button onClick={onBackPress} type="text" icon={<ArrowLeftOutlined className="gray-text" />} />&nbsp;</span>
        }
        {
          modalTitleName && <strong className="gray-text">{modalTitleName}</strong>
        }
      </div> : null)
      }
    >
      <Form
        form={deleteForm}
        layout="vertical"
      >
        <ClaimCancellationComponent handlers={{ form: deleteForm, goNext, setComponentNav }}
          isChecked={isChecked} setIsChecked={setIsChecked}
          handleSubmit={handleSubmit} handleCheckBox={handleCheckBox} onReasonChange={onReasonChange} isLoading={isLoading}
          errorMsg={errorMsg} setErrorMsg={setErrorMsg} buttonText={buttonText} />
      </Form>
    </Modal>
  );
};

export default DeleteClaimModalNew;
